// src/hooks/useProtokoll.js
// Hook und Service zum Anlegen, Laden und Speichern von Wartungsprotokollen.

import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase, withTimeout } from '../lib/supabase';
import { vormerken, inReihe, sendePruefpunkt, offeneAenderungen } from '../lib/warteschlange';

// Erstellt ein neues Protokoll fuer eine Station inkl. Modulen + Pruefpunkten.
//
// Bevorzugt die Datenbankfunktion protokoll_anlegen (Migration 21): EIN Aufruf
// statt acht, und PostgreSQL fuehrt sie als einen Vorgang aus -- entweder das
// Protokoll entsteht vollstaendig oder gar nicht. Vorher konnte ein
// Verbindungsabbruch mitten im Ablauf ein halbes Protokoll hinterlassen:
// ein Eintrag ohne Module oder Module ohne Pruefpunkte, unbrauchbar und
// nicht reparierbar.
//
// Der alte Rueckfallweg (acht Einzelanfragen) ist seit v59 entfernt - die
// Funktion ist seit Migration 21 in der Datenbank.
// art: 'wartung' (Standard) oder 'sichtpruefung' (Migration 47 - weniger Pruefpunkte).
export async function createProtokollForStation(stationId, userId, art = 'wartung') {
  // Ohne erstellt_von_id lehnt die Sicherheitsregel der Datenbank den Datensatz
  // ab ("new row violates row-level security policy"). Die Wartungsplanung im
  // Buero uebergibt keine Nutzer-ID - hier wird sie deshalb nachgeholt.
  let wer = userId;
  if (!wer) {
    try {
      const { data: sess } = await supabase.auth.getSession();
      wer = sess?.session?.user?.id || null;
    } catch { /* offline - dann geht es sowieso nicht */ }
  }
  const { data, error } = await withTimeout(
    supabase.rpc('protokoll_anlegen', { p_station_id: stationId, p_user_id: wer, p_art: art === 'sichtpruefung' ? 'sichtpruefung' : 'wartung' }),
    45000, 'Protokoll anlegen'
  );
  if (error) throw error;
  if (!data) throw new Error('Protokoll wurde nicht angelegt (keine Rückmeldung der Datenbank).');
  return data;
}

const PROTOKOLL_CACHE = 'etb-protokoll-cache-';
// 8 statt 6: Das Vorab-Laden (Startseite) legt bis zu 5 geplante Wartungen ab,
// die zuletzt geoeffneten sollen trotzdem nicht sofort verdraengt werden.
const PROTOKOLL_CACHE_ANZAHL = 8;

// Protokoll mit Modulen und allen Pruefpunkten vom Server. Wirft bei Fehlern -
// alles oder nichts (ein Kapitel ohne Pruefpunkte darf nie entstehen).
export async function protokollVollLaden(protokollId) {
  const { data: prot, error: e1 } = await withTimeout(
    supabase.from('protokolle')
      .select('*, stationen(bezeichnung, standort_strasse, standort_ort, kunden(name, kunden_nr))')
      .eq('id', protokollId).single(),
    20000, 'Protokoll laden'
  );
  if (e1) throw e1;
  const { data: modus, error: e2 } = await withTimeout(
    supabase.from('protokoll_module')
      .select('*, modul_vorlagen(code, name, position, beschreibung)')
      .eq('protokoll_id', protokollId).order('reihenfolge'),
    20000, 'Module laden'
  );
  if (e2) throw e2;
  // Alle Pruefpunkte in EINER Abfrage statt einer je Modul.
  const modulIds = (modus || []).map(m => m.id);
  let alle = [];
  if (modulIds.length) {
    const { data: pps, error: e3 } = await withTimeout(
      supabase.from('protokoll_modul_pruefpunkte')
        .select('*').in('protokoll_modul_id', modulIds).order('reihenfolge'),
      30000, 'Prüfpunkte laden'
    );
    if (e3) throw e3;
    alle = pps || [];
  }
  const module = (modus || []).map(m => ({ ...m, pruefpunkte: alle.filter(p => p.protokoll_modul_id === m.id) }));
  return { protokoll: prot, module };
}

// Stand eines Protokolls auf dem Geraet ablegen (die letzten 8).
//
// Seit v57 schreibt AUCH die Protokollseite hierher, wenn der Monteur etwas
// eingibt. Vorher wurde der Zwischenstand nur nach einem Laden vom Server
// geschrieben: Wurde das Protokoll ohne Netz erneut geoeffnet, standen dort
// die Antworten von VOR der letzten Eingabe - und das naechste Antippen
// schickte alte Zusatzwerte zurueck.
// Erst alte Staende entfernen, dann schreiben: Bei vollem Speicher scheiterte
// sonst das Schreiben, und aufgeraeumt wurde nie.
// nurWennAelterAls (Zeitstempel): Das Vorab-Laden schreibt nur, wenn der
// vorhandene Zwischenstand AELTER ist als der Beginn seines Ladens - sonst
// haette es einen inzwischen auf der Protokollseite getippten Stand ersetzt.
export function protokollZwischenspeichern(protokollId, protokoll, module, { nurWennAelterAls } = {}) {
  if (!protokollId || !protokoll) return false;
  if (nurWennAelterAls) {
    try {
      const vorhanden = JSON.parse(localStorage.getItem(PROTOKOLL_CACHE + protokollId) || 'null');
      if (vorhanden && (vorhanden.stand || 0) >= nurWennAelterAls) return false;
    } catch { /* unlesbar - dann ersetzen */ }
  }
  try {
    let liste = [];
    try { liste = JSON.parse(localStorage.getItem(PROTOKOLL_CACHE + 'liste') || '[]'); } catch { liste = []; }
    if (!Array.isArray(liste)) liste = [];
    liste = liste.filter(x => x !== protokollId);
    liste.unshift(protokollId);
    for (const alt of liste.slice(PROTOKOLL_CACHE_ANZAHL)) localStorage.removeItem(PROTOKOLL_CACHE + alt);
    liste = liste.slice(0, PROTOKOLL_CACHE_ANZAHL);
    localStorage.setItem(PROTOKOLL_CACHE + 'liste', JSON.stringify(liste));
    localStorage.setItem(PROTOKOLL_CACHE + protokollId, JSON.stringify({ protokoll, module, stand: Date.now() }));
    return true;
  } catch {
    // Speicher voll: lieber diesen Zwischenstand opfern als eine Eingabe.
    try { localStorage.removeItem(PROTOKOLL_CACHE + protokollId); } catch { /* egal */ }
    return false;
  }
}

// Hook: Laedt ein bestehendes Protokoll mit allen Modulen + Pruefpunkten
export function useProtokoll(protokollId) {
  const [protokoll, setProtokoll] = useState(null);
  const [module, setModule] = useState([]); // [{...modul, pruefpunkte: [...]}]
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // "Laedt ..." NUR beim ersten Laden. Die Protokollseite zeigt bei loading
  // den ganzen Bildschirm als "Lade Protokoll ..." - ein spaeteres Nachladen
  // (z. B. nach "Trafo nachtragen") raeumte sonst die ganze Seite ab.
  // Gleiche Regel wie in useStation und der Erderanlage (siehe CLAUDE.md).
  const ersteLadung = useRef(true);
  useEffect(() => { ersteLadung.current = true; }, [protokollId]);

  const load = useCallback(async () => {
    if (!protokollId) { setLoading(false); return; }
    // Zuletzt geladener Stand liegt auf dem Geraet: SOFORT zeigen, im
    // Hintergrund frisch laden. Vorher stand bei schwachem Netz "Lade
    // Protokoll ..." - und der Monteur schloss die App (Testlauf 13.09.2026).
    const cacheKey = PROTOKOLL_CACHE + protokollId;
    if (ersteLadung.current) {
      try {
        const g = JSON.parse(localStorage.getItem(cacheKey) || 'null');
        if (g && g.protokoll) {
          const offen = offeneAenderungen();
          setProtokoll(g.protokoll);
          setModule((g.module || []).map(m => ({
            ...m,
            pruefpunkte: (m.pruefpunkte || []).map(p => (offen[p.id] ? { ...p, ...offen[p.id] } : p)),
          })));
          setLoading(false);
          ersteLadung.current = false;
        }
      } catch { /* kein brauchbarer Zwischenstand */ }
    }
    if (ersteLadung.current) setLoading(true);
    setError(null);
    try {
      // Alles oder nichts - bei einem Fehler bleibt der bisherige Stand stehen.
      const { protokoll: prot, module: roh } = await protokollVollLaden(protokollId);
      // Was auf diesem Geraet noch in der Warteschlange liegt, ist NEUER als
      // der Server-Stand. Ohne das sah ein neu geoeffnetes Protokoll leer oder
      // veraltet aus - und die naechste Aenderung schrieb den alten Stand zurueck.
      const offen = offeneAenderungen();
      const enrichedModule = roh.map(m => ({
        ...m,
        pruefpunkte: m.pruefpunkte.map(p => (offen[p.id] ? { ...p, ...offen[p.id] } : p)),
      }));

      setProtokoll(prot);
      setModule(enrichedModule);
      ersteLadung.current = false;
      // Die letzten geoeffneten Protokolle auf dem Geraet behalten.
      protokollZwischenspeichern(protokollId, prot, enrichedModule);
    } catch (e) {
      setError(e.message || 'Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  }, [protokollId]);

  useEffect(() => { load(); }, [load]);

  return { protokoll, module, loading, error, reload: load };
}

// Hook: Liste der Protokolle einer Station (oder alle)
export function useProtokolleByStation(stationId) {
  const [protokolle, setProtokolle] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      let q = supabase.from('protokolle').select('*').order('geaendert_am', { ascending: false });
      if (stationId) q = q.eq('station_id', stationId);
      const { data, error: e } = await q;
      if (e) throw e;
      setProtokolle(data || []);
    } catch (e) {
      setError(e.message || 'Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  }, [stationId]);
  useEffect(() => { load(); }, [load]);
  return { protokolle, loading, error, reload: load };
}

// Pruefpunkt speichern (mit Mangel-Logik)
//
// ppVoll:      kompletter aktueller Stand (fuer den Mangel)
// aenderungen: nur die geaenderten Felder - NUR die werden gesendet.
//
// Erst merken, dann senden (siehe lib/warteschlange.js): Die Eingabe steht
// sofort im Geraetespeicher. Je Pruefpunkt wird streng nacheinander gesendet,
// danach der Mangel abgeglichen - auch fuer Antworten, die offline kamen.
// Ergebnis: { status: 'gesendet' | 'wartet' }. Wirft nur, wenn die Datenbank
// die Antwort wirklich ablehnt.
export async function savePruefpunkt(ppVoll, aenderungen, protokollId, stationId, userId) {
  vormerken(ppVoll, aenderungen, { protokollId, stationId, userId });
  return inReihe(ppVoll.id, () => sendePruefpunkt(ppVoll.id));
}

// Geplante Wartung -> "angefangen". Bisher setzte das niemand: Eine geplante
// Wartung blieb "zugewiesen", auch wenn der Monteur schon 80 Punkte
// ausgefuellt hatte - und verschwand am Tag danach von der Startseite.
// Nur aus vorbereitet/zugewiesen heraus, nie zurueck.
// Ergebnis: 'geaendert' | 'unveraendert' (war schon angefangen) | 'fehler'
export async function alsBegonnenMarkieren(protokollId) {
  if (!protokollId) return 'unveraendert';
  try {
    const { data, error } = await withTimeout(
      supabase.from('protokolle').update({ status: 'entwurf' })
        .eq('id', protokollId).in('status', ['vorbereitet', 'zugewiesen']).select('id'),
      20000, 'Wartung als begonnen markieren'
    );
    if (error) throw error;
    return (data || []).length > 0 ? 'geaendert' : 'unveraendert';
  } catch (e) {
    // Kein Netz: nicht schlimm, die Startseite zeigt ueberfaellige Planungen
    // ohnehin unter "angefangen". Bei der naechsten Eingabe wird es nachgeholt.
    console.warn('[Status] konnte nicht auf "angefangen" gesetzt werden:', e.message);
    return 'fehler';
  }
}

// Trafo waehrend der Wartung nachtragen.
//
// Warum das noetig ist: Das Trafo-Modul entsteht beim Anlegen des Protokolls,
// und nur wenn an der Station schon ein Trafo hinterlegt ist. Steht der Monteur
// vor einem Transformator, den niemand erfasst hat, hatte er bisher gar keine
// Moeglichkeit - kein Modul, kein Knopf, nichts. Er musste die Wartung
// abbrechen und im Buero anrufen.
//
// Legt in einem Zug an:
//   1. den Trafo an der Station (Stammdaten, bleibt dauerhaft)
//   2. das Modul 04-TRAFO-SICHT in DIESEM Protokoll
//   3. die Pruefpunkte dazu
export async function trafoNachtragen(protokollId, stationId, bezeichnung) {
  if (!protokollId || !stationId) throw new Error('Protokoll oder Station fehlt.');

  // Wiederholbar (v63): Scheiterte ein frueherer Versuch mittendrin (Zeitgrenze,
  // Netz weg), werden der schon angelegte Trafo und sein Kapitel wiederverwendet
  // statt "Trafo 3" und ein zweites Kapitel anzulegen.
  const { data: vorlage, error: eVorlage } = await withTimeout(supabase.from('modul_vorlagen')
    .select('id').eq('code', '04-TRAFO-SICHT').single(), 20000, 'Vorlage laden');
  if (eVorlage) throw eVorlage;

  const { data: module, error: eModule } = await withTimeout(supabase.from('protokoll_module')
    .select('id, reihenfolge, bezugs_id, modul_vorlage_id').eq('protokoll_id', protokollId), 20000, 'Kapitel laden');
  if (eModule) throw eModule;
  const naechsteReihe = (module || []).reduce((m, x) => Math.max(m, x.reihenfolge ?? 0), -1) + 1;
  const trafoKapitel = (module || []).filter(m => m.modul_vorlage_id === vorlage.id);

  // 1 · Trafo an der Station
  const { data: vorhandene, error: eVorh } = await withTimeout(supabase.from('station_trafos')
    .select('id, position, bezeichnung, created_at').eq('station_id', stationId).eq('active', true), 20000, 'Trafos laden');
  if (eVorh) throw eVorh;
  const naechstePos = (vorhandene || []).reduce((m, t) => Math.max(m, t.position ?? 0), -1) + 1;
  const vor15Min = Date.now() - 15 * 60 * 1000;
  // Ein gerade eben angelegter Trafo ohne Kapitel in diesem Protokoll = abgebrochener Versuch.
  const halbfertig = (vorhandene || []).find(t => !trafoKapitel.some(m => m.bezugs_id === t.id)
    && t.created_at && new Date(t.created_at).getTime() > vor15Min);
  let trafo = halbfertig || null;
  let modul = null;
  // Oder: Trafo und Kapitel wurden angelegt, aber die Pruefpunkte nicht.
  if (!trafo) {
    for (const m of trafoKapitel) {
      const t = (vorhandene || []).find(x => x.id === m.bezugs_id && x.created_at && new Date(x.created_at).getTime() > vor15Min);
      if (!t) continue;
      const { count, error: eCount } = await withTimeout(supabase.from('protokoll_modul_pruefpunkte')
        .select('id', { count: 'exact', head: true }).eq('protokoll_modul_id', m.id), 20000, 'Prüfpunkte prüfen');
      if (eCount) throw eCount;
      if (!count) { trafo = t; modul = m; break; }
    }
  }
  const name = trafo ? trafo.bezeichnung : ((bezeichnung || '').trim() || ('Trafo ' + (naechstePos + 1)));

  if (!trafo) {
    const { data: neuerTrafo, error: eTrafo } = await withTimeout(
      supabase.from('station_trafos')
        .insert({ station_id: stationId, position: naechstePos, bezeichnung: name })
        .select().single(),
      30000, 'Trafo anlegen'
    );
    if (eTrafo) throw eTrafo;
    trafo = neuerTrafo;
  }

  // 2 · Modul im laufenden Protokoll
  if (!modul) {
  const { data: neuesModul, error: eModul } = await withTimeout(
    supabase.from('protokoll_module').insert({
      protokoll_id: protokollId,
      modul_vorlage_id: vorlage.id,
      bezugs_typ: 'trafo',
      bezugs_id: trafo.id,
      bezugs_label: name,
      reihenfolge: naechsteReihe,
      abgeschlossen: false,
    }).select().single(),
    30000, 'Modul anlegen'
  );
  if (eModul) throw eModul;
  modul = neuesModul;
  }

  // 3 · Pruefpunkte (nur falls das Kapitel noch keine hat). Ueber die Datenbankfunktion aus Migration 21 - so ist
  // sichergestellt, dass alle Felder mitkommen (sichtbar_wenn, logik_invertiert
  // und die Zusatzfelder). Genau da ist frueher schon einmal etwas verloren
  // gegangen.
  const { count: schonDa, error: eSchon } = await withTimeout(supabase.from('protokoll_modul_pruefpunkte')
    .select('id', { count: 'exact', head: true }).eq('protokoll_modul_id', modul.id), 20000, 'Prüfpunkte prüfen');
  // Ohne sichere Antwort NICHT anlegen - die Datenbankfunktion wuerde die
  // Pruefpunkte sonst ein zweites Mal einfuegen.
  if (eSchon) throw eSchon;
  if (!(schonDa > 0)) {
    const { error: ePp } = await withTimeout(
      supabase.rpc('protokoll_pruefpunkte_anlegen', {
        p_modul_instanz_id: modul.id,
        p_modul_vorlage_id: vorlage.id,
      }),
      30000, 'Prüfpunkte anlegen'
    );
    if (ePp) throw ePp;
  }

  // 4 · Oelwanne je Trafo (Migration 70): eigenes Kapitel, wiederholbar.
  await oelwannenKapitelAnlegen(protokollId, trafo, name, naechsteReihe + 1);

  return { trafo, modul };
}

async function oelwannenKapitelAnlegen(protokollId, trafo, name, reihenfolge) {
  const { data: oelVorlage, error: eV } = await withTimeout(supabase.from('modul_vorlagen')
    .select('id').eq('code', '05-OEL-WANNE').eq('active', true).maybeSingle(), 20000, 'Vorlage laden');
  if (eV) throw eV;
  if (!oelVorlage) return;
  const { data: da, error: eDa } = await withTimeout(supabase.from('protokoll_module')
    .select('id').eq('protokoll_id', protokollId).eq('modul_vorlage_id', oelVorlage.id)
    .eq('bezugs_id', trafo.id), 20000, 'Kapitel laden');
  if (eDa) throw eDa;
  let kapitel = (da || [])[0];
  if (!kapitel) {
    const { data: neu, error: eNeu } = await withTimeout(supabase.from('protokoll_module').insert({
      protokoll_id: protokollId,
      modul_vorlage_id: oelVorlage.id,
      bezugs_typ: 'trafo',
      bezugs_id: trafo.id,
      bezugs_label: name,
      reihenfolge,
      abgeschlossen: false,
    }).select().single(), 30000, 'Modul anlegen');
    if (eNeu) throw eNeu;
    kapitel = neu;
  }
  const { count, error: eC } = await withTimeout(supabase.from('protokoll_modul_pruefpunkte')
    .select('id', { count: 'exact', head: true }).eq('protokoll_modul_id', kapitel.id), 20000, 'Prüfpunkte prüfen');
  if (eC) throw eC;
  if (count > 0) return;
  const { error: ePp } = await withTimeout(supabase.rpc('protokoll_pruefpunkte_anlegen', {
    p_modul_instanz_id: kapitel.id, p_modul_vorlage_id: oelVorlage.id,
  }), 30000, 'Prüfpunkte anlegen');
  if (ePp) throw ePp;
}

// === NSHV: weitere Verteilungen (Anna-Lena 08.10.2026, Migration 76) =========
// "Bei der NSHV weitere Verteilungen ergaenzen, z. B. Verteilung 1 - dann die
// Pruefpunkte, dann VT 2 Keller - und dann wieder Pruefpunkte."
//
// Jede weitere Verteilung ist ein eigenes Kapitel 03 NSHV (bezugs_typ
// 'verteilung', bezugs_id = ID der Verteilung) direkt hinter der NSHV. Die
// Verteilung selbst steht in station_konfiguration.nshv_verteilungen - so legt
// protokoll_anlegen sie bei der naechsten Wartung automatisch wieder an.
// Braucht Netz (wie "Trafo nachtragen").
function neueUuid() {
  if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = Math.random() * 16 | 0;
    return (c === 'x' ? r : ((r & 0x3) | 0x8)).toString(16);
  });
}

async function verteilungenDerStation(stationId) {
  const { data, error } = await withTimeout(supabase.from('station_konfiguration')
    .select('station_id, nshv_verteilungen').eq('station_id', stationId).maybeSingle(), 20000, 'Stationskonfiguration laden');
  if (error) throw error;
  return { da: !!data, liste: Array.isArray(data?.nshv_verteilungen) ? data.nshv_verteilungen : [] };
}

async function verteilungenSpeichern(stationId, da, felder) {
  const anfrage = da
    ? supabase.from('station_konfiguration').update(felder).eq('station_id', stationId)
    : supabase.from('station_konfiguration').insert({ station_id: stationId, ...felder });
  const { error } = await withTimeout(anfrage, 20000, 'Verteilungen speichern');
  if (error) throw error;
}

export async function verteilungNachtragen(protokollId, stationId, bezeichnung) {
  if (!protokollId || !stationId) throw new Error('Protokoll oder Station fehlt.');
  const { data: vorlage, error: eVorlage } = await withTimeout(supabase.from('modul_vorlagen')
    .select('id').eq('code', '03-NSHV').single(), 20000, 'Vorlage laden');
  if (eVorlage) throw eVorlage;
  const { data: module, error: eModule } = await withTimeout(supabase.from('protokoll_module')
    .select('id, reihenfolge, modul_vorlage_id').eq('protokoll_id', protokollId), 20000, 'Kapitel laden');
  if (eModule) throw eModule;
  const nshv = (module || []).filter(m => m.modul_vorlage_id === vorlage.id);
  const name = (bezeichnung || '').trim() || ('Verteilung ' + (nshv.length + 1));

  // Platz direkt hinter dem letzten NSHV-Kapitel schaffen (sonst stuende die
  // Verteilung ganz hinten, hinter Erdung und Thermografie).
  const ende = (module || []).reduce((m, x) => Math.max(m, x.reihenfolge ?? 0), -1);
  const reihe = nshv.length ? nshv.reduce((m, x) => Math.max(m, x.reihenfolge ?? 0), -1) + 1 : ende + 1;
  const dahinter = (module || []).filter(m => (m.reihenfolge ?? 0) >= reihe)
    .sort((a, b) => (b.reihenfolge ?? 0) - (a.reihenfolge ?? 0));
  for (const m of dahinter) {
    const { error } = await withTimeout(supabase.from('protokoll_module')
      .update({ reihenfolge: (m.reihenfolge ?? 0) + 1 }).eq('id', m.id), 20000, 'Kapitel verschieben');
    if (error) throw error;
  }

  const id = neueUuid();
  const { data: kapitel, error: eKap } = await withTimeout(supabase.from('protokoll_module').insert({
    protokoll_id: protokollId,
    modul_vorlage_id: vorlage.id,
    bezugs_typ: 'verteilung',
    bezugs_id: id,
    bezugs_label: name,
    reihenfolge: reihe,
    abgeschlossen: false,
  }).select().single(), 30000, 'Kapitel anlegen');
  if (eKap) throw eKap;

  const { error: ePp } = await withTimeout(supabase.rpc('protokoll_pruefpunkte_anlegen', {
    p_modul_instanz_id: kapitel.id, p_modul_vorlage_id: vorlage.id,
  }), 30000, 'Prüfpunkte anlegen');
  if (ePp) throw ePp;
  // "03 NSHV vorhanden" ist bei einer Verteilung klar - schon beantworten.
  await withTimeout(supabase.from('protokoll_modul_pruefpunkte')
    .update({ wert_auswahl: 'vorhanden', in_ordnung: true })
    .eq('protokoll_modul_id', kapitel.id).eq('pos_nummer', '03'), 20000, 'Prüfpunkt 03 setzen')
    .catch(() => { /* nicht schlimm - dann waehlt der Monteur es selbst */ });

  // Zuletzt in die Stammdaten - scheitert das, ist das Kapitel trotzdem da.
  const { da, liste } = await verteilungenDerStation(stationId);
  await verteilungenSpeichern(stationId, da, { nshv_verteilungen: [...liste, { id, bezeichnung: name }] });
  return kapitel;
}

// Bezeichnung einer Verteilung (oder der Haupt-NSHV) aendern: im Kapitel und
// in den Stammdaten der Station.
export async function verteilungUmbenennen(modul, stationId, bezeichnung) {
  const name = (bezeichnung || '').trim();
  const { error } = await withTimeout(supabase.from('protokoll_module')
    .update({ bezugs_label: name || null }).eq('id', modul.id), 20000, 'Bezeichnung speichern');
  if (error) throw error;
  if (!stationId) return;
  if (modul.bezugs_typ === 'verteilung') {
    const { da, liste } = await verteilungenDerStation(stationId);
    if (!liste.some(v => v.id === modul.bezugs_id)) return;
    await verteilungenSpeichern(stationId, da,
      { nshv_verteilungen: liste.map(v => (v.id === modul.bezugs_id ? { ...v, bezeichnung: name } : v)) });
  } else {
    const { da } = await verteilungenDerStation(stationId);
    await verteilungenSpeichern(stationId, da, { nshv_bezeichnung: name || null });
  }
}

// Verteilung wieder entfernen - nur, solange in ihrem Kapitel nichts
// beantwortet ist (ausser dem vorbelegten "03 vorhanden"). Sonst gingen
// Antworten und Maengel verloren.
export async function verteilungEntfernen(modul, stationId) {
  const beantwortet = (modul.pruefpunkte || []).some(pp => String(pp.pos_nummer || '').trim() !== '03'
    && (pp.wert_auswahl != null || pp.wert_zahl != null || pp.wert_bool != null || (pp.wert_text && pp.wert_text.length > 0)));
  if (beantwortet) throw new Error('In dieser Verteilung sind schon Prüfpunkte beantwortet – sie kann nicht mehr entfernt werden.');
  const { error } = await withTimeout(supabase.from('protokoll_module').delete().eq('id', modul.id), 20000, 'Kapitel entfernen');
  if (error) throw error;
  if (!stationId) return;
  const { da, liste } = await verteilungenDerStation(stationId);
  if (liste.some(v => v.id === modul.bezugs_id)) {
    await verteilungenSpeichern(stationId, da, { nshv_verteilungen: liste.filter(v => v.id !== modul.bezugs_id) });
  }
}
